import camelCase from "lodash/camelCase";
import get from "lodash/get";
import { toLuxon } from "../../shared/dateTimeUtils";
import { alphaNumericCompareBy } from "../SnippetReport/SnippetReportStore";

/**
 * Values that can be searched on
 */
export const GROUP_SORT_ACCESSORS = Object.freeze({
  LAST_FIRST: (patient) => `${patient.lastName} ${patient.firstName}`,
  FIRST_LAST: (patient) => `${patient.firstName} ${patient.lastName}`,
  ROOM: "location",
  DOB: ({ calendarDateOfBirth }) => calendarDateOfBirth?.toString(),
  DATETIME: (patient, selectedListOption) =>
    toLuxon(
      moduleSpecifics(patient, selectedListOption).acquisitionDate,
    ).toISO(),
  ECG_COUNT: "count",
  BED: "location",
  ACTIVE_INACTIVE: (patient) =>
    patient.status.toLowerCase() === "active" ? "Active" : "Inactive",
  UNIT: "unit",
});

/**
 *
 * @param {Patient} patient A patient
 * @param {string} selectedListOption The selected list option
 * @returns {string}
 */
export const moduleSpecifics = (patient, selectedListOption) => {
  return (
    patient?.moduleSpecifics ||
    patient?.moduleSpecificData?.find((specificData) => {
      return specificData.moduleId === selectedListOption?.value?.moduleId;
    })
  );
};

/**
 * @param {Object} params
 * @param {Object} params.patient An individual patient
 * @param {string|function} params.sortOption The value to sort by
 * @param {Object} params.selectedListOption An individual patient
 * @returns {string} The property from patient metadata we're sorting with
 */
export const getPatientSortProperty = (params) => {
  let { patient, sortOption, selectedListOption } = params;
  if (GROUP_SORT_ACCESSORS[sortOption]) {
    sortOption = GROUP_SORT_ACCESSORS[sortOption];
  }
  if (typeof sortOption === "function") {
    return sortOption(patient);
  } else {
    const key = camelCase(sortOption);
    return get(
      moduleSpecifics(patient, selectedListOption),
      key,
      get(patient, key),
    );
  }
};

/**
 * Simple Patient sort that accepts a callback function to determine the value to sort with.
 * Defaults to ascending order, but can be inverted by setting isInvertedSort to true
 *
 * @param {Patient[]} patients Array of patients
 * @param {string} sortOption The value to sort off of
 * @param {Object} selectedListOption
 * @param {boolean} isInvertedSort Boolean to return inverted results
 * @returns {Patient[]} A sorted array of patients by sortOption
 */
export const patientSort = (
  patients,
  sortOption,
  selectedListOption,
  isInvertedSort,
) => {
  const accessor = (patient) =>
    getPatientSortProperty({
      patient,
      sortOption: sortOption,
      selectedListOption,
    });

  /**
   * Gets the values from patient metadata we will be comparing against
   *
   * @param {Patient} patientA First patient for comparison
   * @param {Patient} patientB Second patient for comparison
   * @returns {Object} Returns an object with 2 strings that are the values that will be compared
   */
  const getPatientSortValues = (pa, pb) => ({
    patientASortValue: accessor(pa),
    patientBSortValue: accessor(pb),
  });

  const alphaNumericComparator = (options) =>
    alphaNumericCompareBy({ accessor, ...options });
  /**
   * Sorts patients by chosen sortValue in alphaNumeric fashion
   * First, taking the deep-level patient metadata, we isolate the alpha characters only and compare them.
   * Then, if the alpha characters are identical, we then isolate the numeric characters only and compare them.
   *
   * @param {Patients[]} patients Array of patients
   * @returns {number}
   */
  const sortAlphaNumAscending = (patients) => {
    if (!patients) return [];
    patients = [...patients].sort(alphaNumericComparator());
    let reorderedPatients = [];

    if (sortOption === "UNIT") {
      reorderedPatients = [...patients];
      patients.map((patient) => {
        if (patient?.moduleSpecifics?.unit === "{Discharged}") {
          // remove from patients list and add it at the end
          reorderedPatients.splice(reorderedPatients.indexOf(patient), 1);
          reorderedPatients.push(patient);
        }
      });
      return reorderedPatients;
    }
    return [...patients];
  };

  /**
   * Sorts patients by chosen sortValue in alphaNumeric fashion
   * First, taking the deep-level patient metadata, we isolate the alpha characters only and compare them.
   * Then, if the alpha characters are identical, we then isolate the numeric characters only and compare them.
   *
   * @param {Patients[]} patients Array of patients
   * @returns {number}
   */
  const sortAlphaNumDescending = (patients) => {
    patients = [...patients].sort(alphaNumericComparator({ descending: true }));
    let reorderedPatients = [];

    if (sortOption === "UNIT") {
      reorderedPatients = [...patients];
      patients.map((patient) => {
        if (patient?.moduleSpecifics?.unit === "{Discharged}") {
          // remove from patients list and add it at the end
          reorderedPatients.splice(reorderedPatients.indexOf(patient), 1);
          reorderedPatients.unshift(patient);
        }
      });
      return reorderedPatients;
    }
    return [...patients];
  };

  /**
   * Sorts patients by chosen sortValue in Numeric fashion
   * First, taking the deep-level patient metadata, we isolate the alpha characters only and compare them.
   * Then, if the alpha characters are identical, we then isolate the numeric characters only and compare them.
   *
   * @param {Patients[]} patients Array of patients
   * @returns {number}
   */
  const sortNumAscending = (patients) => {
    return (
      patients &&
      [...patients].sort((pa, pb) => {
        const { patientASortValue, patientBSortValue } = getPatientSortValues(
          pa,
          pb,
        );
        if (patientASortValue === null && patientBSortValue === null) return 0;
        if (patientASortValue === undefined && patientBSortValue === undefined)
          return 0;

        // Place undefined and null at the top
        if (patientASortValue === null || patientASortValue === undefined)
          return -1;
        if (patientBSortValue === null || patientBSortValue === undefined)
          return 1;

        return patientASortValue - patientBSortValue;
      })
    );
  };

  /**
   * Sorts patients by chosen sortValue in Numeric fashion
   * First, taking the deep-level patient metadata, we isolate the alpha characters only and compare them.
   * Then, if the alpha characters are identical, we then isolate the numeric characters only and compare them.
   *
   * @param {Patients[]} patients Array of patients
   * @returns {number}
   */

  const sortNumDescending = (patients) => {
    return (
      patients &&
      [...patients].sort((pa, pb) => {
        const { patientASortValue, patientBSortValue } = getPatientSortValues(
          pa,
          pb,
        );

        if (patientASortValue === null && patientBSortValue === null) return 0;
        if (patientASortValue === undefined && patientBSortValue === undefined)
          return 0;

        // Place undefined and null at the bottom
        if (patientASortValue === null || patientASortValue === undefined)
          return 1;
        if (patientBSortValue === null || patientBSortValue === undefined)
          return -1;

        return patientBSortValue - patientASortValue;
      })
    );
  };

  // numeric sort with descending order by default
  const numDesc =
    sortOption === "DILATATION" ||
    sortOption === "EFFACEMENT" ||
    sortOption === "STATION";

  // numeric sort with ascending order by default
  const numAsc = sortOption === "PARA" || sortOption === "GRAVIDA";

  const alphaNumDesc = sortOption === "MEMBRANE_STATUS";

  if (
    (!alphaNumDesc && numDesc && isInvertedSort) ||
    (!alphaNumDesc && numAsc && !isInvertedSort)
  ) {
    return sortNumAscending(patients);
  } else if (
    (!alphaNumDesc && numDesc && !isInvertedSort) ||
    (!alphaNumDesc && numAsc && isInvertedSort)
  ) {
    return sortNumDescending(patients);
  } else if (
    (alphaNumDesc && isInvertedSort) ||
    (!alphaNumDesc && !numDesc && !numAsc && !isInvertedSort)
  ) {
    return sortAlphaNumAscending(patients);
  } else {
    return sortAlphaNumDescending(patients);
  }
};
